import React from 'react';
import {styled} from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import type {Bag} from "../../services/classes/Bag";
import {Checkbox, FormControlLabel, FormGroup, Grid, IconButton, OutlinedInput, Typography} from "@mui/material";
import {AdminPanelSettings, Check, Edit} from "@mui/icons-material";
import {SelectDatasetMenu} from "../../components/_tiny/SelectDatasetMenu";
import {useStores} from "../../stores";

const PREFIX = 'DataSetSelectEditComponent';

const classes = {
    formControl: `${PREFIX}-formControl`,
    selectEmpty: `${PREFIX}-selectEmpty`,
    formEditIcon: `${PREFIX}-formEditIcon`
};

const StyledGrid = styled(Grid)(({theme}) => ({
    [`& .${classes.formControl}`]: {
        margin: theme.spacing(1),
        minHeight: '4em',
    },

    [`& .${classes.selectEmpty}`]: {
        marginTop: theme.spacing(2),
    },

    [`& .${classes.formEditIcon}`]: {
        margin: theme.spacing(1),
    }
}));

const menuProps = {
    id: 'main-databag-menu',
};

type Props = {
    enableEditName?: boolean,
    bags?: Bag[],
    selectedBagId?: number,
    disabled?: boolean,
    onSelectBag: (b: Bag) => void,
    onPatchBag: (b: Bag) => void,
}
export const DataSetSelectEditComponent: React.FC<Props> = (
    {
        enableEditName,
        bags,
        selectedBagId,
        disabled,
        onSelectBag,
        onPatchBag
    }) => {
    const {authStore} = useStores();

    const getBag = (id) => bags?.find(b => b.id === id) as Bag;
    const getSelectedBagName = () => {
        const b = getBag(selectedBagId);
        return b?.name || '';
    }
    const getSelectedBagHidden = () => {
        const b = getBag(selectedBagId);
        return b?.hidden ?? null;
    }

    const onSelectionChange = (event) => {
        const bagId = event.target.value;
        onSelectBag(getBag(bagId));
    };

    // Internal state:
    const [isEdit, setIsEdit] = React.useState<boolean>(false);
    const [editName, setEditName] = React.useState<string>('');
    const [bagIsHidden, setBagIsHidden] = React.useState<null | boolean>(null);

    // Internal actions:
    const onEditNameChange = (event) => setEditName(event.target.value);
    const onClickEdit = () => {
        setEditName(getSelectedBagName());
        setBagIsHidden(getSelectedBagHidden())
        setIsEdit(true)
    }
    const handleSave = () => {
        // Patch the current bag
        const oldName = getSelectedBagName();
        const b = getBag(selectedBagId);
        if (oldName !== editName || bagIsHidden !== b.hidden) {
            const patchedBag: Bag = {...b, name: editName};
            if (bagIsHidden !== null) {
                patchedBag.hidden = bagIsHidden;
            }
            onPatchBag(patchedBag);
        }
    };
    const onClickSave = () => {
        handleSave()
        setIsEdit(false)
    }

    const nameLabel = 'Name of dataset'
    return <>
        <StyledGrid container className="form-element dataset-select-component dataset-select-edit-component">
            <Grid>
                {isEdit ? <FormControl className={classes.formControl} variant="outlined">
                        <InputLabel id="dataset-name-label" htmlFor="dataset-name">
                            {nameLabel}
                        </InputLabel>
                        <form onSubmit={onClickSave}>
                            <OutlinedInput
                                id="dataset-name"
                                value={editName}
                                onChange={onEditNameChange}
                                label={nameLabel}
                                disabled={disabled}/>
                        </form>
                    </FormControl>
                    : bags ? <FormControl className={classes.formControl} variant="standard">
                        <InputLabel id="selected-bag-label">
                            Existing datasets
                        </InputLabel>
                        <SelectDatasetMenu
                            variant="standard"
                            labelId="selected-bag-label"
                            id="selected-bag"
                            value={selectedBagId || ''}
                            onChange={onSelectionChange}
                            // displayEmpty
                            className={classes.selectEmpty + ' select-bag-field'}
                            disabled={disabled}
                            bags={bags}
                            MenuProps={menuProps}
                        />
                        {/*<FormHelperText>Label + placeholder</FormHelperText>*/}
                    </FormControl> : <>
                        <InputLabel id="selected-bag-loading-label">
                            &nbsp;
                        </InputLabel>
                        <Select
                            variant="standard"
                            labelId="selected-bag-loading-label"
                            id="selected-bag-loading"
                            className="select-bag-field is-loading"
                            value="Loading..."
                            disabled>
                            <MenuItem value="Loading..."/>
                        </Select>
                    </>
                }
            </Grid>
            <Grid>
                {enableEditName && <>
                    {!isEdit && <IconButton
                        className={classes.formEditIcon}
                        aria-label="button edit"
                        onClick={onClickEdit}
                        disabled={!bags || disabled}>
                        <Edit/>
                    </IconButton>}
                    {isEdit && <IconButton
                        className={classes.formEditIcon}
                        aria-label="button save"
                        onClick={onClickSave}
                        disabled={!bags || disabled}>
                        <Check/>
                    </IconButton>}
                </>}
            </Grid>
        </StyledGrid>

        {/*Extra staff option to hide/unhide databags*/}
        {isEdit && authStore.isMithraStaffGroup && isEdit && <>
            <Typography variant="body1" gutterBottom>
                <AdminPanelSettings/> Databag id: {'' + selectedBagId}
            </Typography>
            <FormGroup>
                <FormControlLabel
                    value="end"
                    control={<Checkbox
                        checked={Boolean(bagIsHidden)}
                        indeterminate={bagIsHidden === null}
                        onChange={(e) => setBagIsHidden(e.target.checked)}
                    />}
                    label={<><AdminPanelSettings/>Hide this databag</>}
                    labelPlacement="end"
                />
            </FormGroup>
        </>}

    </>
}
